/**
 * Shared Admin Page Styled Components
 * 
 * This file provides consistent styled components for all admin pages.
 * Import these components instead of defining page-specific styles.
 */

import styled, { css } from 'styled-components';
import { theme, blueGrey, neutral, jadeGreen, garnetRed, goldenTopaz, lightBlue } from '../design-system/theme';

// ============================================================================
// PAGE LAYOUT
// ============================================================================

export const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const PageTitle = styled.h1`
  color: ${theme.textPrimary};
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

export const PageDescription = styled.p`
  color: ${theme.textSecondary};
  margin: 4px 0 0 0;
  font-size: 14px;
`;

export const PageSubtitle = styled.p`
  color: ${theme.textSecondary};
  margin: 4px 0 0 0;
  font-size: 14px;
`;

export const PageTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// ============================================================================
// FILTERS & SEARCH
// ============================================================================

export const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  padding: 10px 16px;
  border: 2px solid ${neutral[4]};
  border-radius: 8px;
  font-size: 14px;
  min-width: 220px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: ${theme.focusRing};
  }

  &::placeholder {
    color: ${theme.textSubtle};
  }
`;

export const SelectInput = styled.select`
  padding: 10px 16px;
  border: 2px solid ${neutral[4]};
  border-radius: 8px;
  font-size: 14px;
  min-width: 160px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: ${theme.focusRing};
  }
`;

// ============================================================================
// BUTTONS
// ============================================================================

const buttonBase = css`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: ${theme.primary};
  color: white;

  &:hover:not(:disabled) {
    background: ${blueGrey[9]};
    transform: translateY(-1px);
    box-shadow: ${theme.shadowHover};
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  background: ${neutral[2]};
  color: ${theme.textPrimary};

  &:hover:not(:disabled) {
    background: ${neutral[3]};
  }
`;

export const DangerButton = styled.button`
  ${buttonBase}
  background: ${garnetRed[5]};
  color: white;

  &:hover:not(:disabled) {
    background: ${garnetRed[7]};
  }
`;

export const SuccessButton = styled.button`
  ${buttonBase}
  background: ${jadeGreen[5]};
  color: white;

  &:hover:not(:disabled) {
    background: ${jadeGreen[6]};
  }
`;

export const OutlineButton = styled.button`
  ${buttonBase}
  background: transparent;
  border: 2px solid ${neutral[4]};
  color: ${theme.textPrimary};

  &:hover:not(:disabled) {
    border-color: ${theme.primary};
    background: ${neutral[1]};
  }
`;

// Icon buttons for table actions
export const IconButton = styled.button<{ $variant?: 'view' | 'edit' | 'danger' | 'success' }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => {
    switch (props.$variant) {
      case 'view': return lightBlue[1];
      case 'edit': return goldenTopaz[1];
      case 'danger': return garnetRed[1];
      case 'success': return jadeGreen[1];
      default: return neutral[2];
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'view': return lightBlue[7];
      case 'edit': return goldenTopaz[7];
      case 'danger': return garnetRed[6];
      case 'success': return jadeGreen[6];
      default: return theme.textPrimary;
    }
  }};

  &:hover:not(:disabled) {
    opacity: 0.8;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

// ============================================================================
// TABLE - Clean design with clear borders
// ============================================================================

export const Table = styled.table`
  width: 100%;
  background-color: white;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${neutral[3]};
  border-radius: 8px;
  overflow: hidden;
`;

export const Th = styled.th`
  background: ${neutral[2]};
  color: ${theme.textPrimary};
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 1px solid ${neutral[3]};
  border-right: 1px solid ${neutral[3]};
  
  &:last-child {
    border-right: none;
  }
`;

export const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${neutral[3]};
  border-right: 1px solid ${neutral[3]};
  font-size: 13px;
  color: ${theme.textPrimary};
  vertical-align: middle;
  
  &:last-child {
    border-right: none;
  }
`;

export const Tr = styled.tr`
  transition: background 0.15s ease;

  &:hover {
    background-color: ${neutral[1]};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
`;

// ============================================================================
// BADGES & TAGS
// ============================================================================

export const StatusBadge = styled.span<{ $status: string }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => {
    switch (props.$status?.toUpperCase()) {
      case 'ACTIVE':
      case 'PUBLISHED':
      case 'COMPLETED':
      case 'APPROVED':
        return jadeGreen[1];
      case 'DRAFT':
      case 'PENDING':
      case 'IN_PROGRESS':
        return goldenTopaz[1];
      case 'INACTIVE':
      case 'ARCHIVED':
      case 'EXPIRED':
        return neutral[3];
      case 'REJECTED':
      case 'WITHDRAWN':
      case 'ERROR':
        return garnetRed[1];
      default:
        return lightBlue[1];
    }
  }};
  color: ${props => {
    switch (props.$status?.toUpperCase()) {
      case 'ACTIVE':
      case 'PUBLISHED':
      case 'COMPLETED':
      case 'APPROVED':
        return jadeGreen[7];
      case 'DRAFT':
      case 'PENDING':
      case 'IN_PROGRESS':
        return goldenTopaz[8];
      case 'INACTIVE':
      case 'ARCHIVED':
      case 'EXPIRED':
        return neutral[7];
      case 'REJECTED':
      case 'WITHDRAWN':
      case 'ERROR':
        return garnetRed[6];
      default:
        return lightBlue[7];
    }
  }};
`;

export const Tag = styled.span`
  background: ${lightBlue[1]};
  color: ${lightBlue[7]};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

export const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// ============================================================================
// CARDS
// ============================================================================

export const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

export const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${neutral[3]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CardBody = styled.div`
  padding: 24px;
`;

export const CardFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${neutral[3]};
  background: ${neutral[1]};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

// ============================================================================
// MODAL
// ============================================================================

export const ModalOverlay = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

export const ModalContent = styled.div`
  background: white;
  padding: 0;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${neutral[3]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${theme.textPrimary};
`;

export const ModalBody = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

export const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${neutral[3]};
  background: ${neutral[1]};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${theme.textSecondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${neutral[2]};
    color: ${theme.textPrimary};
  }
`;

// ============================================================================
// FORM
// ============================================================================

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.textPrimary};
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.$hasError ? garnetRed[4] : neutral[4]};
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? garnetRed[5] : theme.primary};
    box-shadow: ${props => props.$hasError ? `0 0 0 3px ${garnetRed[2]}` : theme.focusRing};
  }

  &:disabled {
    background: ${neutral[2]};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.textSubtle};
  }
`;

export const Textarea = styled.textarea<{ $hasError?: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.$hasError ? garnetRed[4] : neutral[4]};
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: ${theme.focusRing};
  }

  &::placeholder {
    color: ${theme.textSubtle};
  }
`;

export const Select = styled.select<{ $hasError?: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.$hasError ? garnetRed[4] : neutral[4]};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? garnetRed[5] : theme.primary};
    box-shadow: ${props => props.$hasError ? `0 0 0 3px ${garnetRed[2]}` : theme.focusRing};
  }
`;

export const FormSection = styled.div`
  border: 1px solid ${neutral[3]};
  border-radius: 12px;
  padding: 20px;
`;

export const FormSectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${theme.textPrimary};
  padding-bottom: 12px;
  border-bottom: 1px solid ${neutral[3]};
`;

export const HelpText = styled.span`
  font-size: 12px;
  color: ${theme.textSubtle};
`;

export const ErrorText = styled.span`
  font-size: 12px;
  color: ${garnetRed[5]};
`;

// ============================================================================
// MESSAGES
// ============================================================================

export const AlertMessage = styled.div<{ $variant?: 'success' | 'error' | 'warning' | 'info' }>`
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return jadeGreen[1];
      case 'error': return garnetRed[1];
      case 'warning': return goldenTopaz[1];
      default: return lightBlue[1];
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return jadeGreen[7];
      case 'error': return garnetRed[6];
      case 'warning': return goldenTopaz[8];
      default: return lightBlue[7];
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'success': return jadeGreen[3];
      case 'error': return garnetRed[3];
      case 'warning': return goldenTopaz[4];
      default: return lightBlue[3];
    }
  }};
`;

// Convenience aliases for common message types
export const SuccessMessage = styled(AlertMessage).attrs({ $variant: 'success' as const })``;
export const ErrorMessage = styled(AlertMessage).attrs({ $variant: 'error' as const })``;
export const WarningMessage = styled(AlertMessage).attrs({ $variant: 'warning' as const })``;
export const InfoMessage = styled(AlertMessage).attrs({ $variant: 'info' as const })``;

// TextArea alias (with capital A for consistency)
export const TextArea = Textarea;

// ============================================================================
// EMPTY STATE
// ============================================================================

export const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.textSecondary};
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export const EmptyStateTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${theme.textPrimary};
`;

export const EmptyStateText = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: ${theme.textSecondary};
`;

// Alias for EmptyState
export const EmptyState = EmptyStateContainer;

// ============================================================================
// LOADING
// ============================================================================

export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  color: ${theme.textSecondary};
  font-size: 16px;
`;

// ============================================================================
// STATS - Subtle inline stats row
// ============================================================================

export const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const StatPill = styled.div<{ $highlight?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.$highlight ? blueGrey[1] : neutral[1]};
  border: 1px solid ${props => props.$highlight ? blueGrey[3] : neutral[3]};
  border-radius: 6px;
  font-size: 13px;
  
  .value {
    font-weight: 700;
    color: ${props => props.$highlight ? theme.primary : theme.textPrimary};
  }
  
  .label {
    color: ${theme.textSecondary};
    font-weight: 400;
  }
`;

// Legacy support - keeping old names for backward compatibility
export const StatsGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const StatCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${neutral[1]};
  border: 1px solid ${neutral[3]};
  border-radius: 6px;
  font-size: 13px;
`;

export const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.textSecondary};
`;

export const StatValue = styled.span<{ $color?: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.$color || theme.textPrimary};
`;

export const StatSubtext = styled.span`
  font-size: 11px;
  color: ${theme.textSubtle};
`;

// ============================================================================
// PAGINATION
// ============================================================================

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${theme.textSecondary};
`;

export const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border-radius: 6px;
  border: ${props => props.$active ? 'none' : `1px solid ${neutral[4]}`};
  background: ${props => props.$active ? theme.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? theme.primaryHover : neutral[2]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// TABS
// ============================================================================

export const TabContainer = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${neutral[3]};
  margin-bottom: 24px;
`;

export const Tab = styled.button<{ $active?: boolean }>`
  padding: 12px 24px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  border-bottom: 2px solid ${props => props.$active ? theme.primary : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;

  &:hover {
    color: ${theme.primary};
  }
`;

// ============================================================================
// UTILITIES
// ============================================================================

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${neutral[3]};
  margin: 24px 0;
`;

export const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Spacer = styled.div<{ $size?: number }>`
  height: ${props => props.$size || 24}px;
`;

export const TruncatedText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
`;

export const MonoText = styled.code`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  background: ${neutral[2]};
  padding: 4px 8px;
  border-radius: 4px;
  color: ${theme.textPrimary};
`;

// ============================================================================
// MODAL VARIANTS
// ============================================================================

export const ModalContentLarge = styled(ModalContent)`
  max-width: 900px;
`;

export const ModalContentXL = styled(ModalContent)`
  max-width: 1200px;
`;

export const ModalBodyScrollable = styled(ModalBody)`
  max-height: 70vh;
  overflow-y: auto;
`;

// ============================================================================
// INFO GRID (for detail views)
// ============================================================================

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const InfoLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const InfoValue = styled.span`
  font-size: 15px;
  color: ${theme.textPrimary};
`;

// ============================================================================
// TAB BUTTONS (pill style)
// ============================================================================

export const TabButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  background: ${neutral[2]};
  padding: 4px;
  border-radius: 8px;
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: ${theme.primary};
  }
`;

// ============================================================================
// FILTER BUTTONS
// ============================================================================

export const FilterButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.$active ? theme.primary : neutral[4]};
  border-radius: 20px;
  background: ${props => props.$active ? blueGrey[1] : 'white'};
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.primary};
    color: ${theme.primary};
  }
`;

// ============================================================================
// ROLE & VERIFIED BADGES
// ============================================================================

export const RoleBadge = styled.span<{ $role?: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.$role?.toUpperCase()) {
      case 'SUPER_ADMIN':
      case 'SUPERADMIN':
        return garnetRed[1];
      case 'ADMIN':
        return blueGrey[1];
      case 'MANAGER':
        return goldenTopaz[1];
      default:
        return neutral[2];
    }
  }};
  color: ${props => {
    switch (props.$role?.toUpperCase()) {
      case 'SUPER_ADMIN':
      case 'SUPERADMIN':
        return garnetRed[6];
      case 'ADMIN':
        return blueGrey[7];
      case 'MANAGER':
        return goldenTopaz[8];
      default:
        return theme.textSecondary;
    }
  }};
`;

export const VerifiedBadge = styled.span<{ $verified?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$verified ? jadeGreen[1] : neutral[2]};
  color: ${props => props.$verified ? jadeGreen[7] : theme.textSecondary};

  &::before {
    content: '${props => props.$verified ? '✓' : '○'}';
  }
`;

// ============================================================================
// ACTION BADGES (for audit logs, etc.)
// ============================================================================

export const ActionBadge = styled.span<{ $action?: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    const action = props.$action?.toUpperCase();
    if (action?.includes('CREATE') || action?.includes('ADD')) return jadeGreen[1];
    if (action?.includes('UPDATE') || action?.includes('EDIT')) return goldenTopaz[1];
    if (action?.includes('DELETE') || action?.includes('REMOVE')) return garnetRed[1];
    if (action?.includes('VIEW') || action?.includes('READ')) return lightBlue[1];
    return neutral[2];
  }};
  color: ${props => {
    const action = props.$action?.toUpperCase();
    if (action?.includes('CREATE') || action?.includes('ADD')) return jadeGreen[7];
    if (action?.includes('UPDATE') || action?.includes('EDIT')) return goldenTopaz[8];
    if (action?.includes('DELETE') || action?.includes('REMOVE')) return garnetRed[6];
    if (action?.includes('VIEW') || action?.includes('READ')) return lightBlue[7];
    return theme.textSecondary;
  }};
`;

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

export const Section = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ============================================================================
// FORM ROW VARIANTS
// ============================================================================

export const FormRow2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormRow3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// ============================================================================
// DROPDOWN BUTTON (for headers/toolbars)
// ============================================================================

export const DropdownButton = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${theme.textSecondary};
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${neutral[2]};
    color: ${theme.textPrimary};
  }
`;

// ============================================================================
// NOTIFICATION BADGE
// ============================================================================

export const NotificationBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${garnetRed[5]};
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ============================================================================
// ACTIVITY LIST
// ============================================================================

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${neutral[1]};
  border-radius: 8px;
`;

export const ActivityIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${neutral[3]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

export const ActivityDetails = styled.div`
  flex: 1;
`;

export const ActivityTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.textPrimary};
`;

export const ActivityTime = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
`;
